import InsertPhotoOutlinedIcon from '@mui/icons-material/InsertPhotoOutlined'
import { Box, Chip, Tooltip, Typography, styled, useTheme } from '@mui/material'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'
import { useTranslation } from 'react-i18next'
import { MdDelete, MdEdit } from 'react-icons/md'
import { FileUrlLoader } from 'src/components/FileUploader'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { IPositionType } from 'src/generated-types'
import { MUIThemeColors } from 'src/mui-theme'
import { getThemeColor } from 'src/utils'

const PREVIEW_SIZE = 32

const PreviewPlaceholder = styled(Box)(({ theme }) => ({
  width: PREVIEW_SIZE,
  height: PREVIEW_SIZE,
  borderRadius: 4,
  backgroundColor: theme.palette.grey[100],
  border: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

const PreviewImageFrame = styled(Box)({
  width: PREVIEW_SIZE,
  height: PREVIEW_SIZE,
  overflow: 'hidden',
  borderRadius: 4,
})

const OpsActionsRow = styled(Box)({
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
  gap: 4,
  paddingRight: 4,
})

const CategoryChip = styled(Chip)(({ theme }) => ({
  height: 22,
  borderRadius: 4,
  backgroundColor: getThemeColor(theme, MUIThemeColors.paleBlue),
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.3px',
  textTransform: 'uppercase',
  '& .MuiChip-label': { paddingLeft: 8, paddingRight: 8 },
}))

export const PositionDrawingPreviewCell: React.FC<GridRenderCellParams> = ({ value }) => {
  if (!value) {
    return (
      <PreviewPlaceholder>
        <InsertPhotoOutlinedIcon sx={{ fontSize: 18, color: 'grey.400' }} />
      </PreviewPlaceholder>
    )
  }

  return (
    <PreviewImageFrame onClick={e => e.stopPropagation()}>
      <FileUrlLoader
        fileUrl={value as string}
        openOnClick
        className="w-full h-full object-cover"
      />
    </PreviewImageFrame>
  )
}

export const PositionDrawingNumberCell: React.FC<GridRenderCellParams> = ({ value }) => {
  const theme = useTheme()
  return (
    <Typography
      variant="body1"
      fontWeight={700}
      color={getThemeColor(theme, MUIThemeColors.primaryDark)}
      noWrap
    >
      {value}
    </Typography>
  )
}

export const PositionDrawingCategoryCell: React.FC<GridRenderCellParams> = ({ value }) => {
  const { t } = useTranslation(['position'])
  const label = t(IPositionType[value as IPositionType], { ns: 'position' })

  return <CategoryChip label={label} size="small" />
}

export const PositionDrawingProductsCell: React.FC<GridRenderCellParams> = ({ row }) => {
  const products = row.positionDrawingProductNumbers
  const text =
    !products || products.length === 0 ? '' : products.map((item: { productNumber: string }) => item.productNumber).join(', ')

  if (!text) {
    return (
      <Typography variant="body1" color="text.disabled">
        —
      </Typography>
    )
  }

  return (
    <Tooltip title={text} placement="top-start">
      <Typography variant="body1" color="text.primary" noWrap sx={{ width: '100%' }}>
        {text}
      </Typography>
    </Tooltip>
  )
}

interface PositionDrawingOpsCellProps extends GridRenderCellParams {
  onEdit: (id: EntityId) => void
  onDelete: (id: EntityId) => void
  disabled?: boolean
}

const withStopPropagation =
  (action: () => void) => (event: React.MouseEvent) => {
    event.stopPropagation()
    action()
  }

export const PositionDrawingOpsCell: React.FC<PositionDrawingOpsCellProps> = ({
  id,
  onEdit,
  onDelete,
  disabled,
}) => {
  const { t } = useTranslation(['common'])

  return (
    <OpsActionsRow>
      <GridActionButton
        toolTipTitle={t('edit', { ns: 'common' })}
        buttonIcon={<MdEdit size={18} />}
        onClick={withStopPropagation(() => onEdit(id))}
        disabled={disabled}
        width="32px"
        height="32px"
        shadowWeight={null}
        border={0}
      />
      <GridActionButton
        toolTipTitle={t('delete', { ns: 'common' })}
        buttonIcon={<MdDelete size={18} />}
        onClick={withStopPropagation(() => onDelete(id))}
        disabled={disabled}
        width="32px"
        height="32px"
        shadowWeight={null}
        border={0}
      />
    </OpsActionsRow>
  )
}
